"use client";

import { useState, useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import {
  Field,
  FieldSet,
  FieldLegend,
  FieldGroup,
  FieldContent,
  FieldLabel,
  FieldTitle,
  FieldDescription,
  FieldSeparator,
  FieldError,
} from "../../@/components/ui/field";
import { Input } from "../../@/components/ui/input";
import { Textarea } from "../../@/components/ui/textarea";
import MarkdownEditorDialog from "../components/MarkdownEditorDialog";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../@/components/ui/select";
import { Plus, Info, UploadCloud, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api_endpoint } from "../constants/constant";
import axios from "axios";
import { useAuth, useUser } from "@clerk/clerk-react";
import { fetchAllPaginatedItems } from "@/lib/fetchAllPaginatedItems";
import { ClipLoader } from "react-spinners";
import { cn } from "@/lib/utils";
import { useR2Upload } from "@/lib/useR2Upload";
import { Form, FormField } from "../components/ui/form";

const courseFormSchema = z.object({
  title: z.string().min(1),
  price: z.string().min(1),
  slug: z
    .string()
    .min(1)
    .max(10, "Slug must be at most 10 characters")
    .regex(/^\S+$/, "Course slug cannot contain spaces"),
  summary: z
    .string()
    .min(1)
    .refine((val) => val.trim().split(/\s+/).length <= 50, {
      message: "Summary must be at most 50 words",
    }),
  description: z.string().min(1),
  // allow either a URL string or a File / anything optional so selecting a file doesn't break zod validation
  thumbnail: z.any().optional(),
  pathways: z.array(z.string()).optional(),
  instructors: z.array(z.string()).optional(),
  managers: z.array(z.string()).optional(),
  admins: z.array(z.string()).optional(),
  difficulty: z.enum(["Beginner", "Intermediate", "Advanced"]),
  status: z.enum(["Draft", "Published"]),
  isOpen: z.enum(["0", "1"]),
});

const DIFFICULTY_MAP = {
  Beginner: 1,
  Intermediate: 2,
  Advanced: 3,
};

const STATUS_MAP = {
  Draft: 0,
  Published: 1,
};

export function NewCourseDialog({ refetchCourses }) {
  const [open, setOpen] = useState(false);
  const [pathways, setPathways] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [isLoadingPathways, setIsLoadingPathways] = useState(false);
  const [loadingInstructors, setLoadingInstructors] = useState(false);
  const [existingSlugs, setExistingSlugs] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [markdownEditorOpen, setMarkdownEditorOpen] = useState(false);
  const [managers, setManagers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);

  const form = useForm({
    resolver: zodResolver(courseFormSchema),
    defaultValues: {
      title: "",
      price: "",
      slug: "",
      summary: "",
      description: "",
      thumbnail: "",
      pathways: [],
      instructors: [],
      managers: [],
      admins: [],
      difficulty: "Beginner",
      status: "Draft",
      isOpen: "0",
    },
  });

  const { getToken, userId, isLoaded } = useAuth();
  const { user } = useUser();
  const { uploadFile } = useR2Upload();
  const creatorId = String(userId || user?.id || "").trim();
  const creatorLabel =
    `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
    user?.primaryEmailAddress?.emailAddress ||
    creatorId;

  // Always keep the course creator represented as an instructor (similar to class owner in attendees).
  useEffect(() => {
    if (!open) return;
    if (!creatorId) return;
    const current = (form.getValues("instructors") || []).map(String);
    if (!current.includes(creatorId)) {
      form.setValue("instructors", [...current, creatorId], {
        shouldValidate: true,
      });
    }
  }, [open, creatorId, form]);

  const processForm = async (values) => {
    try {
      if (!isLoaded || !creatorId) {
        toast.error("Your session is still loading. Please try again in a moment.");
        return;
      }

      const token = await getToken();
      if (!token) throw new Error("No auth token found");

      let thumbnailUrl = values.thumbnail || "";
      if (thumbnailFile) {
        try {
          const rawSlug = String(values.slug || "untitled").trim();
          const safeSlug = rawSlug
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "");

          const ext = String(thumbnailFile.name || "")
            .split(".")
            .pop()
            ?.toLowerCase();
          const fileExt = ext && /^[a-z0-9]+$/.test(ext) ? ext : "jpg";

          const objectKey = `assets/thumbnails/courses/courses-${
            safeSlug || "untitled"
          }/thumbnail.${fileExt}`;

          const internalBucketName =
            import.meta.env.VITE_R2_INTERNAL_BUCKET_NAME ||
            import.meta.env.VITE_BUCKET_NAME;
          const internalPublicUrl =
            import.meta.env.VITE_R2_INTERNAL_PUBLIC_URL ||
            import.meta.env.VITE_R2_PUBLIC_URL;

          const uploadedUrl = await uploadFile(thumbnailFile, objectKey, {
            silent: true,
            bucketName: internalBucketName,
            publicUrlBase: internalPublicUrl,
            cacheBust: true,
          });

          if (!uploadedUrl) {
            toast.error("Thumbnail upload failed. Please try again.");
            return;
          }

          thumbnailUrl = uploadedUrl;
        } catch (uploadErr) {
          console.error("Thumbnail upload error:", uploadErr);
          toast.error("Thumbnail upload failed. Please try again.");
          return;
        }
      }

      // Map difficulty and status to backend as integer values
      const resolvedInstructorIds = Array.from(
        new Set([
          ...((values.instructors || []).map((id) => String(id).trim()) || []),
          ...(creatorId ? [creatorId] : []),
        ])
      ).filter(Boolean);

      const payload = {
        title: values.title,
        slug: values.slug,
        summary: values.summary,
        description: values.description,
        thumbnail: thumbnailUrl,
        // Remove any non-digit / non-dot characters then parse
        price: parseFloat(String(values.price).replace(/[^\d.]/g, "")),
        difficulty: DIFFICULTY_MAP[values.difficulty],
        status: STATUS_MAP["Draft"],
        isOpen: Number(values.isOpen),
        // Map pathway titles to IDs
        pathways: values.pathways.map((id) => Number(id)),

        // Persist selected instructor IDs (always includes creator).
        instructors: resolvedInstructorIds,

        managers: managers
          .filter((m) => values.managers.includes(m.id))
          .map((m) => m.id),

        admins: admins
          .filter((a) => (values.admins ?? []).includes(a.id))
          .map((a) => a.id),
      };

      console.log("Submitting Course Payload:", payload);

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.post(
        api_endpoint + "/course/create",
        payload,
        reqConfig
      );
      console.log("New Course:", response.data);
      toast.success(`Created "${values.title}" successfully!`);
      setOpen(false);
      form.reset();
      setThumbnailFile(null);
      setThumbnailPreview(null);

      if (refetchCourses) {
        refetchCourses();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string" && detail.toLowerCase().includes("creator user not found")) {
        toast.error("Creator user not found. Your account may not be provisioned in the backend yet.");
        return;
      }
      console.error(
        "Error creating course:",
        err?.response?.data || err.message
      );
      toast.error("Failed to create course. Please try again.");
    }
  };

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      await processForm(values);
    } catch (err) {
      console.error(
        "Error creating course:",
        err?.response?.data || err.message
      );
      toast.error("Failed to create course. Please try again.");
    }
    setIsSubmitting(false);
  };
  // get the pathways
  const fetchPathways = async () => {
    try {
      setIsLoadingPathways(true);
      const response = await axios.get(api_endpoint + "/pathway/");

      if (response.data && response.data.pathways) {
        setPathways(response.data.pathways);
      } else {
        console.warn("Unexpected API response structure:", response.data);
        setPathways([]);
      }
    } catch (error) {
      console.error("Error fetching pathways:", error);
      toast.error("Failed to load pathways");
      setPathways([]);
    } finally {
      setIsLoadingPathways(false);
    }
  };

  const fetchInstructors = async () => {
    try {
      setLoadingInstructors(true);
      const token = await getToken();
      if (!token) throw new Error("No auth token found");

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
      const instructors = await fetchAllPaginatedItems({
        url: api_endpoint + `/user/users/role/${"Instructor"}`,
        token,
        selectItems: (data) => data?.users ?? [],
        mode: "skip",
        pageSize: 200,
        headers: reqConfig.headers,
      });
      setInstructors(instructors || []);
    } catch (error) {
      console.error("Failed to fetch Instructors:", error);
      setInstructors([]);
    } finally {
      setLoadingInstructors(false);
    }
  };

  const fetchManagers = async () => {
    try {
      setLoadingManagers(true);
      const token = await getToken();
      if (!token) throw new Error("No auth token found");

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
      const managers = await fetchAllPaginatedItems({
        url: api_endpoint + `/user/users/role/${"Manager"}`,
        token,
        selectItems: (data) => data?.users ?? [],
        mode: "skip",
        pageSize: 200,
        headers: reqConfig.headers,
      });
      setManagers(managers || []);
    } catch (error) {
      console.error("Failed to fetch Managers:", error);
      setManagers([]);
    } finally {
      setLoadingManagers(false);
    }
  };

  const fetchAdmins = async () => {
    try {
      setLoadingAdmins(true);
      const token = await getToken();
      if (!token) throw new Error("No auth token found");

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
      const admins = await fetchAllPaginatedItems({
        url: api_endpoint + `/user/users/role/${"Admin"}`,
        token,
        selectItems: (data) => data?.users ?? [],
        mode: "skip",
        pageSize: 200,
        headers: reqConfig.headers,
      });
      setAdmins(admins || []);
    } catch (error) {
      console.error("Failed to fetch Admins:", error);
      setAdmins([]);
    } finally {
      setLoadingAdmins(false);
    }
  };

  // Fetch pathways and instructors only when dialog is opened
  const handleOpenChange = (isOpen) => {
    setOpen(isOpen);
    if (isOpen && pathways.length === 0 && instructors.length === 0) {
      fetchPathways();
      fetchInstructors();
      fetchManagers();
      fetchAdmins();
    }
  };

  // Only close the markdown editor, not the main dialog
  const handleMarkdownSave = (newContent) => {
    form.setValue("description", newContent);
    setMarkdownEditorOpen(false);
  };

  const handleMarkdownEditorClose = () => {
    setMarkdownEditorOpen(false);
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setThumbnailFile(file);
      const previewUrl = URL.createObjectURL(file);
      setThumbnailPreview(previewUrl);
      // update RHF form value so validation doesn't fail
      form.setValue("thumbnail", previewUrl);
    } else {
      setThumbnailFile(null);
      setThumbnailPreview(null);
      form.setValue("thumbnail", "");
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          <Button variant="" className="w-full md:w-auto">
            New Course
            <Plus className="w-4 h-4" />
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="">Create New Course</DialogTitle>
            <DialogDescription className="">
              Fill in the details to create a new course for your students
            </DialogDescription>
          </DialogHeader>
          <form
            id="new-course-form"
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6"
          >
            <FormField
              control={form.control}
              name="title"
              render={({ field, fieldState }) => (
                <Field>
                  <FieldLabel>Course Title*</FieldLabel>
                  <FieldContent>
                    <Input
                      {...field}
                      placeholder="Python Programming"
                      className={cn(
                        "mt-1 h-10",
                        fieldState.error &&
                          "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                      )}
                    />
                    <FieldError
                      errors={
                        fieldState.error ? [{ message: "This is required" }] : []
                      }
                    />
                  </FieldContent>
                </Field>
              )}
            />

            <div className="w-full grid grid-cols-2 gap-8">
              <FormField
                control={form.control}
                name="price"
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel>Price*</FieldLabel>
                    <FieldContent>
                      <Input
                        {...field}
                        placeholder="KES 30,000"
                        className={cn(
                          "mt-1 h-10",
                          fieldState.error &&
                            "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                        )}
                      />
                      <FieldError
                        errors={
                          fieldState.error
                            ? [{ message: "This is required" }]
                            : []
                        }
                      />
                    </FieldContent>
                  </Field>
                )}
              />

              <FormField
                control={form.control}
                name="slug"
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel>Course Slug*</FieldLabel>
                    <FieldContent>
                      <Input
                        {...field}
                        placeholder="Python"
                        className={cn(
                          "mt-1 h-10",
                          fieldState.error &&
                            "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                        )}
                      />
                      
                      <p className="mt-1 text-xs text-(--neutral-500) font-body">
                       <Info className="w-3 h-3 inline-block mr-1" /> Course slugs are unique identifiers of a course. <span className="font-bold text-(--color-destructive)">Do not
                        include spaces.</span>
                      </p>
                      <FieldError
                        errors={
                          fieldState.error
                            ? [{ message: fieldState.error.message || "This is required" }]
                            : []
                        }
                      />
                    </FieldContent>
                  </Field>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="summary"
              render={({ field, fieldState }) => {
                const wordCount = field.value
                  ? field.value.trim().split(/\s+/).length
                  : 0;
                return (
                  <Field>
                    <FieldLabel>Course Summary*</FieldLabel>
                    <FieldContent>
                      <Textarea
                        {...field}
                        placeholder="Brief summary for course overview"
                        rows={2}
                        className={cn(
                          "mt-1",
                          fieldState.error &&
                            "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                        )}
                      />
                      <div
                        className={`text-xs mt-1 ${
                          wordCount > 50
                            ? "text-(--color-destructive)"
                            : "text-(--neutral-600)"
                        }`}
                      >
                        {wordCount} / 50 words
                      </div>
                      <FieldError
                        errors={
                          fieldState.error
                            ? [{ message: "This is required" }]
                            : []
                        }
                      />
                    </FieldContent>
                  </Field>
                );
              }}
            />

            <FormField
              control={form.control}
              name="thumbnail"
              render={({ field }) => (
                <Field>
                  <FieldLabel>
                    Course Thumbnail
                    <div className="text-xs text-(--neutral-600) font-body">
                      Need icons? Get them from{" "}
                      <a
                        href="https://undraw.co/"
                        target="_blank"
                        rel="noreferrer"
                        className="underline underline-offset-2 text-(--color-primary)"
                      >
                        unDraw
                      </a>
                    </div>
                  </FieldLabel>
                  <FieldContent>
                    {thumbnailPreview ? (
                      <div className="relative w-full h-48 rounded-lg overflow-hidden border">
                        <img
                          src={thumbnailPreview}
                          alt="Thumbnail preview"
                          className="w-full h-full object-cover"
                        />
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          className="absolute top-2 right-2 h-8 w-8"
                          onClick={() => {
                            setThumbnailFile(null);
                            setThumbnailPreview(null);
                            form.setValue("thumbnail", "");
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-48 border border-dashed rounded-lg cursor-pointer bg-(--neutral-50) hover:bg-(--neutral-100)">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <UploadCloud className="w-8 h-8 mb-4 text-(--neutral-600)" />
                          <p className="mb-2 text-sm text-(--neutral-600)">
                            <span className="font-semibold">
                              Click to upload
                            </span>{" "}
                            or drag and drop
                          </p>
                          <p className="text-xs text-(--neutral-600)">
                            SVG, PNG, JPG or GIF
                          </p>
                        </div>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={handleThumbnailChange}
                          className="hidden"
                        />
                      </label>
                    )}
                  </FieldContent>
                </Field>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field, fieldState }) => {
                return (
                  <Field>
                    <div className="flex items-center justify-between">
                      <FieldLabel>
                        Course Description*
                        <div className="flex items-center gap-1 text-xs text-(--neutral-600) font-body">
                          <Info className="w-3 h-3" />
                          Markdown supported
                        </div>
                      </FieldLabel>
                      <Button
                        type="button"
                        onClick={() => setMarkdownEditorOpen(true)}
                        size=""
                        variant="outline"
                        className=""
                      >
                        Edit in Fullscreen
                      </Button>
                    </div>
                    <FieldContent>
                      <Textarea
                        {...field}
                        placeholder="Provide a detailed description of what students will learn"
                        rows={8}
                        className={cn(
                          "mt-1",
                          fieldState.error &&
                            "border-(--color-destructive) focus-visible:ring-(--color-primary)"
                        )}
                      />
                    </FieldContent>
                    <FieldError
                      errors={
                        fieldState.error ? [{ message: "This is required" }] : []
                      }
                    />
                  </Field>
                );
              }}
            />

            <FormField
              control={form.control}
              name="pathways"
              render={({ field, fieldState }) => (
                <Field>
                  <FieldLabel>
                    Associated Pathways
                    <div className="flex items-center gap-1 text-xs text-(--neutral-600) font-body">
                      <Info className="w-3 h-3" />
                      Optional
                    </div>
                  </FieldLabel>
                  <FieldContent>
                    <Select
                      onValueChange={(value) => {
                        if (value && !field.value.includes(value)) {
                          field.onChange([...field.value, value]);
                        }
                      }}
                      value=""
                      disabled={isLoadingPathways}
                    >
                      <SelectTrigger
                        className={cn(
                          "select-trigger mt-1 h-10 w-full font-body",
                          fieldState.error &&
                            "border-(--color-destructive) focus:ring-(--color-destructive)"
                        )}
                      >
                        <SelectValue
                          placeholder={
                            isLoadingPathways
                              ? "Loading pathways..."
                              : "Select all that apply"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent className="select-content">
                        {pathways
                          .filter(
                            (pathway) =>
                              !field.value.includes(String(pathway.id))
                          )
                          .map((pathway) => (
                            <SelectItem
                              key={pathway.id}
                              value={String(pathway.id)}
                            >
                              {pathway.title}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </FieldContent>
                  {field.value.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {field.value.map((pathwayId) => {
                        const pathway = pathways.find(
                          (p) => String(p.id) === pathwayId
                        );
                        return (
                          <div key={pathwayId} className="tag-chip">
                            <span className="font-body">
                              {pathway ? pathway.title : pathwayId}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                field.onChange(
                                  field.value.filter((p) => p !== pathwayId)
                                );
                              }}
                              className="chip-remove"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <FieldError
                    errors={
                      fieldState.error ? [{ message: "This is required" }] : []
                    }
                  />
                </Field>
              )}
            />

            <FormField
              control={form.control}
              name="instructors"
              render={({ field, fieldState }) => (
                <Field>
                  <FieldLabel>
                    Instructors
                    <div className="flex items-center gap-1 text-xs text-(--neutral-600) font-body">
                      <Info className="w-3 h-3" />
                      Optional
                    </div>
                  </FieldLabel>
                  <FieldContent>
                    <Select
                      onValueChange={(value) => {
                        const nextId = String(value || "").trim();
                        if (nextId && !field.value.map(String).includes(nextId)) {
                          field.onChange([...field.value.map(String), nextId]);
                        }
                      }}
                      value=""
                      disabled={loadingInstructors}
                    >
                      <SelectTrigger
                        className={cn(
                          "select-trigger mt-1 h-10 w-full font-body",
                          fieldState.error &&
                            "border-(--color-destructive) focus:ring-(--color-destructive)"
                        )}
                      >
                        <SelectValue
                          placeholder={
                            loadingInstructors
                              ? "Loading instructors..."
                              : "Select all that apply"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent className="select-content">
                        {instructors
                          .filter(
                            (instructor) =>
                              !field.value.map(String).includes(String(instructor.id))
                          )
                          .map((instructor) => (
                            <SelectItem
                              key={instructor.id}
                              value={String(instructor.id)}
                              className="select-item font-body"
                            >
                              {instructor.first_name} {instructor.last_name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </FieldContent>
                  {field.value.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {field.value.map((instructorId) => {
                        const normalizedId = String(instructorId);
                        const instructor = instructors.find(
                          (i) => String(i.id) === normalizedId
                        );
                        const isCreatorChip = creatorId && normalizedId === creatorId;
                        const label =
                          isCreatorChip
                            ? creatorLabel
                            : instructor
                              ? `${instructor.first_name} ${instructor.last_name}`
                              : normalizedId;
                        return (
                          <div key={normalizedId} className="tag-chip">
                            <span className="font-body">{label}</span>
                            {!isCreatorChip && (
                              <button
                                type="button"
                                onClick={() => {
                                  field.onChange(
                                    field.value
                                      .map(String)
                                      .filter((id) => id !== normalizedId)
                                  );
                                }}
                                className="chip-remove"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <FieldError
                    errors={
                      fieldState.error ? [{ message: "This is required" }] : []
                    }
                  />
                </Field>
              )}
            />

            <FormField
              control={form.control}
              name="managers"
              render={({ field, fieldState }) => (
                <Field>
                  <FieldLabel className="">
                    Managers
                    <div className="flex items-center gap-1 text-xs text-(--nuetral-500) font-body">
                      <Info className="w-3 h-3" />
                      Optional
                    </div>
                  </FieldLabel>
                  <FieldContent>
                    <Select
                      onValueChange={(value) => {
                        if (value && !field.value.includes(value)) {
                          field.onChange([...field.value, value]);
                        }
                      }}
                      value=""
                      disabled={loadingManagers}
                    >
                      <SelectTrigger
                        className={cn(
                          "select-trigger mt-1 h-10 w-full font-body",
                          fieldState.error &&
                            "border-(--color-destructive) focus:ring-(--color-destructive)"
                        )}
                      >
                        <SelectValue
                          placeholder={
                            loadingManagers
                              ? "Loading managers..."
                              : "Select all that apply"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent className="select-content">
                        {managers
                          .filter(
                            (manager) =>
                              !field.value.includes(String(manager.id))
                          )
                          .map((manager) => (
                            <SelectItem
                              key={manager.id}
                              value={manager.id}
                              className="select-item font-body"
                            >
                              {manager.first_name} {manager.last_name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </FieldContent>
                  {field.value.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {field.value.map((managerId) => {
                        const manager = managers.find(
                          (m) => m.id === managerId
                        );
                        return (
                          <div key={managerId} className="tag-chip">
                            <span className="font-body">
                              {manager
                                ? `${manager.first_name} ${manager.last_name}`
                                : managerId}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                field.onChange(
                                  field.value.filter((id) => id !== managerId)
                                );
                              }}
                              className="chip-remove"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <FieldError
                    errors={
                      fieldState.error ? [{ message: "This is required" }] : []
                    }
                  />
                </Field>
              )}
            />

            {/* <FormField
                  control={form.control}
                  name="admins"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-black font-heading">
                        Admins
                        <div className="flex items-center gap-1 text-xs text-gray-500 font-body">
                          <Info className="w-3 h-3" />
                          Optional
                        </div>
                      </FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={(value) => {
                            if (value && !field.value.includes(value)) {
                              field.onChange([...field.value, value]);
                            }
                          }}
                          value=""
                          disabled={loadingAdmins}
                        >
                          <SelectTrigger
                            className="select-trigger mt-1 h-10 w-full font-body">
                            <SelectValue
                              placeholder={
                                loadingAdmins
                                  ? "Loading admins..."
                                  : "Select all that apply"
                              }
                            />
                          </SelectTrigger>
                          <SelectContent className="select-content">
                            {admins.map((admin) => (
                              <SelectItem
                                key={admin.id}
                                value={admin.id}
                                className="select-item font-body"
                              >
                                {admin.first_name} {admin.last_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      {field.value.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {field.value.map((adminId) => {
                            const admin = admins.find((a) => a.id === adminId);
                            return (
                              <div
                                key={adminId}
                                className="tag-chip"
                              >
                                <span className="font-body">
                                  {admin
                                    ? `${admin.first_name} ${admin.last_name}`
                                    : adminId}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    field.onChange(
                                      field.value.filter((id) => id !== adminId)
                                    );
                                  }}
                                  className="chip-remove"
                                >
                                  ×
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                /> */}

            <div className="w-full grid grid-cols-2 gap-8">
              <FormField
                control={form.control}
                name="difficulty"
                render={({ field, fieldState }) => (
                  <Field className="w-full">
                    <FieldLabel>Difficulty*</FieldLabel>
                    <FieldContent>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <SelectTrigger
                          className={cn(
                            "select-trigger mt-1 h-10 w-full font-body",
                            fieldState.error &&
                              "border-(--color-destructive) focus:ring-(--color-destructive)"
                          )}
                        >
                          <SelectValue placeholder="Select Difficulty" />
                        </SelectTrigger>
                        <SelectContent className="select-content">
                          <SelectItem value="Beginner">Beginner</SelectItem>
                          <SelectItem value="Intermediate">
                            Intermediate
                          </SelectItem>
                          <SelectItem value="Advanced">Advanced</SelectItem>
                        </SelectContent>
                      </Select>
                    </FieldContent>
                    <FieldError
                      errors={
                        fieldState.error ? [{ message: "This is required" }] : []
                      }
                    />
                  </Field>
                )}
              />

              <FormField
                control={form.control}
                name="isOpen"
                className="w-full"
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel>Course Visibility*</FieldLabel>
                    <FieldContent>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <SelectTrigger
                          className={cn(
                            "select-trigger mt-1 h-10 w-full font-body",
                            fieldState.error &&
                              "border-(--color-destructive) focus:ring-(--color-destructive)"
                          )}
                          data-focus-red
                        >
                          <SelectValue placeholder="Select visibility" />
                        </SelectTrigger>
                        <SelectContent className="select-content">
                          <SelectItem value="1">Open</SelectItem>
                          <SelectItem value="0">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </FieldContent>
                    <FieldError
                      errors={
                        fieldState.error ? [{ message: "This is required" }] : []
                      }
                    />
                  </Field>
                )}
              />

              {/* <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-black font-heading">
                          Course Status*
                        </FormLabel>
                        <div
                          className="mt-1 h-10 flex items-center px-3 border rounded-[8px] font-body text-gray-500 select-none opacity-80 cursor-not-allowed"
                          aria-disabled="true"
                          tabIndex={-1}
                        >
                          Draft
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  /> */}
            </div>
          </form>

          <DialogFooter className="">
            <DialogClose asChild>
              <Button variant="outline" className="">
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              variant=""
              form="new-course-form"
              className={cn("", isSubmitting && "btn-disabled")}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center w-full">
                  <ClipLoader size={20} color="#fff" />
                </span>
              ) : (
                "Create Course"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="w-full h-full">
        <MarkdownEditorDialog
          open={markdownEditorOpen}
          initialValue={form.watch("description")}
          onClose={handleMarkdownEditorClose}
          onSave={handleMarkdownSave}
          contentFolder={"courses"}
          contentName={form.watch("slug")}
        />
      </div>
    </>
  );
}
